import { useLayoutEffect, useRef, useState } from 'react';
import { Card, Descriptions, Segmented, Space, Tag, Typography } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { countText, platformLabels, reviewedCount } from './helpers';
import PushImage from './push_image';
import CustomDataPreview from './custom_data_preview';
import WebNotificationPreview from './web_notification_preview';
import { customDataValues } from './custom_data';
import { pushLandingUrl } from './landing_url';

const APP_ICON = '/images/goai-app-icon.png';
const IOS_CARD_WIDTH = 400;

function IosNotificationPreview({ content, imageUrl, rtl }) {
    const viewport = useRef(null);
    const card = useRef(null);
    const [size, setSize] = useState({ width: IOS_CARD_WIDTH, height: 110 });
    useLayoutEffect(() => {
        const measure = () => {
            const next = { width: viewport.current?.clientWidth || IOS_CARD_WIDTH, height: card.current?.offsetHeight || 110 };
            setSize((old) => old.width === next.width && old.height === next.height ? old : next);
        };
        measure();
        if (globalThis.ResizeObserver) {
            const observer = new ResizeObserver(measure);
            if (viewport.current) observer.observe(viewport.current);
            if (card.current) observer.observe(card.current);
            return () => observer.disconnect();
        }
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [content.title, content.body, imageUrl]);
    const scale = Math.min(1, size.width / IOS_CARD_WIDTH);
    return <div className="push-notification-device push-ios-preview-stage">
        <div className="push-device-clock">09:00</div>
        <div ref={viewport} className="push-ios-preview-viewport" style={{ height: size.height * scale }}>
            <div ref={card} className="push-notification-bubble push-ios-notification" aria-label="通知卡片预览" style={{ width: IOS_CARD_WIDTH, transform: `scale(${scale})` }}>
                <img className="push-notification-icon" src={APP_ICON} alt="GoAI App 图标" />
                <div className="push-notification-copy" dir={rtl ? 'rtl' : 'auto'}>
                    <strong>{content.title || '通知标题'}</strong>
                    <p>{content.body || '填写正文后在这里预览'}</p>
                </div>
                <div className="push-notification-trailing"><time>现在</time><PushImage url={imageUrl} thumbnail /></div>
            </div>
        </div>
    </div>;
}

export default function NotificationPreview({ campaign, metadata, locale, estimate }) {
    const [platform, setPlatform] = useState('ios');
    const content = campaign.contents?.find((v) => v.locale === locale) || {};
    const language = metadata.locales?.find((v) => v.code === locale);
    const data = customDataValues(campaign);
    const type = metadata.types?.find((v) => v.code === data.type);
    const landingUrl = pushLandingUrl(data, metadata.environment);
    const webHost = ['prod', 'production'].includes(metadata.environment) ? 'app.goai.digital' : 'app.dev.goai.digital';
    return <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card title="通知预览" size="small" extra={<Tag>{language?.name || locale}</Tag>}>
            <Segmented block aria-label="通知预览平台" value={platform} onChange={setPlatform} options={['ios', 'android', 'web'].filter((value) => metadata.platforms?.includes(value)).map((value) => ({ value, label: platformLabels[value] }))} />
            {platform === 'ios' ? <IosNotificationPreview content={content} imageUrl={campaign.imageUrl} rtl={language?.rtl} /> : platform === 'web' ? <WebNotificationPreview content={content} host={webHost} rtl={language?.rtl} /> : <div className="push-notification-device">
                <div className="push-device-clock">09:00</div>
                <div className="push-notification-bubble" aria-label="通知卡片预览">
                    <img className="push-notification-icon" src={APP_ICON} alt="GoAI App 图标" />
                    <div className="push-notification-copy" dir={language?.rtl ? 'rtl' : 'auto'}>
                        <strong>{content.title || '通知标题'}</strong>
                        <p>{content.body || '填写正文后在这里预览'}</p>
                    </div>
                    <div className="push-notification-trailing"><time>现在</time><PushImage url={campaign.imageUrl} thumbnail /></div>
                </div>
            </div>}
            <Typography.Paragraph type="secondary" className="push-preview-caption">{platform === 'ios' ? '按实机截图样式预览；系统字体大小与通知状态会影响实际显示。' : platform === 'web' ? '电脑端 Chrome 通知中心示意，实际样式以系统和浏览器为准。' : '展示效果示意，实际样式以设备和浏览器为准。'}</Typography.Paragraph>
            <div className="push-landing"><Space><LinkOutlined />点击通知后会进入页面</Space><div>{platform === 'web' && data.type === 'POST' ? 'Assistant（Web 暂不支持文章详情）' : landingUrl ? <Typography.Link href={landingUrl} target="_blank" rel="noopener noreferrer" style={{ overflowWrap: 'anywhere' }}>{landingUrl}</Typography.Link> : <>{type?.name} · 尚未选择目标</>}</div></div>
        </Card>
        <Card title="附加参数预览" size="small"><CustomDataPreview campaign={campaign} /></Card>
        <Card title="发送摘要" size="small">
            <Descriptions size="small" column={1} items={[
                { key: 'locales', label: '文案确认', children: `${reviewedCount(campaign)} / ${metadata.locales?.length || 0}` },
                { key: 'platforms', label: '目标平台', children: campaign.platforms?.map((v) => platformLabels[v]).join(' / ') || '尚未选择' },
                { key: 'devices', label: '预计设备', children: estimate ? `${countText(estimate.devices)} 台` : '等待估算' },
                { key: 'users', label: '预计用户', children: estimate ? `${countText(estimate.users)} 人` : '等待估算' },
            ]} />
        </Card>
    </Space>;
}
